const memoryCache = new Map();
const inflightRequests = new Map();

// ─── Backend health tracking ───────────────────────────────────────────────
let _downSignals = 0;
let _backendDownAt = null;
let _healthPollTimer = null;
const DOWN_THRESHOLD = 2;
const HEALTH_URL = "/auth/online-count";
const POLL_MS = 5000;

function _startPoll() {
  if (_healthPollTimer) return;
  _healthPollTimer = setInterval(async () => {
    if (!navigator.onLine) return;
    try {
      const res = await fetch(HEALTH_URL, { cache: "no-store" });
      if (res.ok || res.status < 500) _markBackendUp();
    } catch {}
  }, POLL_MS);
}

function _stopPoll() {
  clearInterval(_healthPollTimer);
  _healthPollTimer = null;
}

function _markBackendDown() {
  if (_backendDownAt) return;
  if (!navigator.onLine) return;
  _backendDownAt = Date.now();
  window.dispatchEvent(new CustomEvent("audassist:backend-down", { detail: { ts: _backendDownAt } }));
  _startPoll();
}

function _markBackendUp() {
  if (!_backendDownAt) return;
  _backendDownAt = null;
  _downSignals = 0;
  _stopPoll();
  window.dispatchEvent(new CustomEvent("audassist:backend-up"));
}

export function recordApiSuccess() {
  _downSignals = 0;
  _markBackendUp();
}

export function recordApiDownSignal() {
  if (!navigator.onLine) return;
  _downSignals++;
  if (_downSignals >= DOWN_THRESHOLD) _markBackendDown();
}

if (typeof window !== "undefined") {
  window.addEventListener("offline", () => {
    _stopPoll();
    _downSignals = 0;
  });
  window.addEventListener("online", () => {
    _downSignals = 0;
    if (_backendDownAt) _startPoll();
  });
}
// ──────────────────────────────────────────────────────────────────────────

function looksLikeMojibake(value) {
  return /Р[\u0400-\u04ffA-Za-z]/.test(value) || value.includes("вЂ");
}

function tryFixMojibake(value) {
  if (!value || !looksLikeMojibake(value)) return value;
  try {
    const bytes = Uint8Array.from(String(value), (char) => char.charCodeAt(0) & 0xff);
    const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return decoded && decoded !== value ? decoded : value;
  } catch {
    return value;
  }
}

function normalizeErrorMessage(raw, fallbackStatus = "") {
  if (!raw) return fallbackStatus ? `Request failed ${fallbackStatus}` : "Request failed";

  if (typeof raw === "object") {
    if (typeof raw.detail === "string") return tryFixMojibake(raw.detail);
    if (typeof raw.message === "string") return tryFixMojibake(raw.message);
  }

  let text = String(raw).trim();
  if (!text) return fallbackStatus ? `Request failed ${fallbackStatus}` : "Request failed";

  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") {
      return normalizeErrorMessage(parsed, fallbackStatus);
    }
  } catch {
    // ignore non-JSON text
  }

  const pythonDetailMatch = text.match(/^[{[]?\s*['"]detail['"]\s*:\s*['"]([\s\S]*?)['"]\s*[}\]]?$/);
  if (pythonDetailMatch?.[1]) {
    text = pythonDetailMatch[1];
  }

  return tryFixMojibake(text);
}

function now() {
  return Date.now();
}

function isFresh(entry, ttlMs) {
  if (!entry) return false;
  if (!Number.isFinite(ttlMs) || ttlMs < 0) return true;
  return now() - entry.ts <= ttlMs;
}

export function peekCachedJson(cacheKey, ttlMs = 0) {
  const entry = memoryCache.get(cacheKey);
  if (!isFresh(entry, ttlMs)) return null;
  return entry.data;
}

export function pruneStaleCache(maxAgeMs = 7 * 24 * 60 * 60 * 1000) {
  const cutoff = Date.now() - maxAgeMs;
  try {
    const toDelete = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("cache:")) continue;
      try {
        const parsed = JSON.parse(localStorage.getItem(key));
        if (parsed?.ts && parsed.ts < cutoff) toDelete.push(key);
      } catch {}
    }
    toDelete.forEach(k => { try { localStorage.removeItem(k); } catch {} });
  } catch {}
}

export function storeCachedJson(cacheKey, data) {
  memoryCache.set(cacheKey, { data, ts: now() });
  return data;
}

export function invalidateCachedJson(matcher) {
  const matches =
    !matcher
      ? () => true
      : typeof matcher === "function"
        ? matcher
        : (key) => key === matcher || key.startsWith(String(matcher));

  for (const key of memoryCache.keys()) {
    if (matches(key)) memoryCache.delete(key);
  }
  for (const key of inflightRequests.keys()) {
    if (matches(key)) inflightRequests.delete(key);
  }

  // also purge matching entries from localStorage
  try {
    const toDelete = [];
    for (let i = 0; i < localStorage.length; i++) {
      const lsKey = localStorage.key(i);
      if (!lsKey?.startsWith("cache:")) continue;
      const cacheKey = lsKey.slice("cache:".length);
      if (matches(cacheKey)) toDelete.push(lsKey);
    }
    toDelete.forEach(k => { try { localStorage.removeItem(k); } catch {} });
  } catch {}
}

function backgroundRefresh(url, cacheKey, headers, fetchOptions) {
  if (inflightRequests.has(cacheKey)) return;
  const p = (async () => {
    try {
      const res = await fetch(url, { ...fetchOptions, headers });
      if (!res.ok) return;
      const json = await res.json();
      storeCachedJson(cacheKey, json);
      try {
        localStorage.setItem(`cache:${cacheKey}`, JSON.stringify({ data: json, ts: Date.now() }));
      } catch {}
      window.dispatchEvent(
        new CustomEvent("audassist:cache-refreshed", { detail: { cacheKey, data: json } })
      );
    } catch {}
  })();
  inflightRequests.set(cacheKey, p);
  p.finally(() => inflightRequests.delete(cacheKey));
}

export async function fetchJsonCached(
  url,
  {
    cacheKey = url,
    ttlMs = 0,
    headers = {},
    fetchOptions = {},
    force = false,
    swr = false,
  } = {}
) {
  if (!force) {
    const fresh = peekCachedJson(cacheKey, ttlMs);
    if (fresh !== null) return fresh;

    // SWR: устаревшие данные → отдаём сразу + обновляем в фоне
    if (swr) {
      const staleEntry = memoryCache.get(cacheKey);
      if (staleEntry) {
        backgroundRefresh(url, cacheKey, headers, fetchOptions);
        return staleEntry.data;
      }
      // холодный старт: прогреваем из localStorage
      try {
        const raw = localStorage.getItem(`cache:${cacheKey}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          const data = parsed?.data !== undefined ? parsed.data : parsed;
          if (data != null) {
            storeCachedJson(cacheKey, data);
            backgroundRefresh(url, cacheKey, headers, fetchOptions);
            return data;
          }
        }
      } catch {}
    }
  }

  if (inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey);
  }

  const request = (async () => {
    const MAX_RETRIES = 2;
    let lastErr = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const res = await fetch(url, { ...fetchOptions, headers });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          if (res.status === 502 || res.status === 503 || res.status === 504) {
            recordApiDownSignal();
          }
          // HTTP-ошибки не ретраим — сразу в localStorage
          lastErr = Object.assign(new Error(normalizeErrorMessage(txt, res.status)), { isHttpError: true });
          break;
        }

        const json = await res.json();
        recordApiSuccess();
        storeCachedJson(cacheKey, json);
        try {
          localStorage.setItem(`cache:${cacheKey}`, JSON.stringify({ data: json, ts: Date.now() }));
        } catch {}
        return json;

      } catch (err) {
        lastErr = err;
        if (!err.isHttpError && attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, 700 * (attempt + 1)));
          continue;
        }
        // Network error after all retries — signal backend may be down
        if (!err.isHttpError) recordApiDownSignal();
        break;
      }
    }

    // Уровень 1: localStorage
    try {
      const raw = localStorage.getItem(`cache:${cacheKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        const data = parsed?.data !== undefined ? parsed.data : parsed;
        const ts = parsed?.ts ?? null;
        window.dispatchEvent(new CustomEvent("audassist:offline-fallback", { detail: { cacheKey, ts } }));
        return data;
      }
    } catch {}

    // Уровень 2: Cache Storage (заполняется SW через Periodic Background Sync)
    try {
      if ("caches" in window) {
        const apiCache = await caches.open("audassist-api-v1");
        const cached = await apiCache.match(url);
        if (cached) {
          const ts = parseInt(cached.headers.get("X-Cached-At") || "0", 10) || null;
          const data = await cached.json();
          try {
            localStorage.setItem(`cache:${cacheKey}`, JSON.stringify({ data, ts }));
          } catch {}
          window.dispatchEvent(new CustomEvent("audassist:offline-fallback", { detail: { cacheKey, ts } }));
          return data;
        }
      }
    } catch {}

    throw lastErr;
  })();

  inflightRequests.set(cacheKey, request);

  try {
    return await request;
  } finally {
    inflightRequests.delete(cacheKey);
  }
}
